import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
// eslint-disable-next-line no-unused-vars
import { collection, addDoc, Timestamp, query, where, getDocs, orderBy, writeBatch, doc, onSnapshot } from 'firebase/firestore';
import { parseCSV, cleanCurrency } from '../utils/csvParser';
import {
    IoCloudUploadOutline, IoSaveOutline, IoCheckmarkDoneOutline,
    // eslint-disable-next-line no-unused-vars
    IoFilterOutline, IoSearchOutline, IoWalletOutline, IoArrowBack, IoClose, IoPerson, IoReceipt, IoAlertCircleOutline,
    IoCaretUpOutline, IoCaretDownOutline, IoFunnelOutline
} from 'react-icons/io5';
import moment from 'moment';
import './GestionBancariaScreen.css';
import KondoSidebarMenu from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import { useAuth } from '../context/AuthContext';
import { getDoc } from 'firebase/firestore';
import ClasificarGastoModal from '../components/ClasificarGastoModal';


function GestionBancariaScreen() {
    const { userData } = useAuth();
    const [condoData, setCondoData] = useState(null);
    const [isPremiumLoading, setIsPremiumLoading] = useState(false);

    // --- Tabs State ---
    const [activeTab, setActiveTab] = useState('listado'); // listado | importar

    // Helper to calculate balance
    const getSaldo = (charge) => {
        return charge.monto - (charge.montoPagado || 0);
    };

    // --- Import Wizard State ---
    const [csvData, setCsvData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [mapping, setMapping] = useState({ fecha: '', descripcion: '', monto: '', referencia: '', residente: '' });
    const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Review/Save
    const [loading, setLoading] = useState(false);
    const [belvoScriptLoaded, setBelvoScriptLoaded] = useState(false);
    const [isBelvoSyncing, setIsBelvoSyncing] = useState(false);

    useEffect(() => {
        if (window.Belvo) {
            setBelvoScriptLoaded(true);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://cdn.belvo.co/belvo-widget/v2/index.js';
        script.async = true;
        script.onload = () => {
            setBelvoScriptLoaded(true);
        };
        script.onerror = (e) => {
            console.error("Failed to load Belvo Connect Widget script", e);
        };
        document.body.appendChild(script);
    }, []);

    const handleOpenBelvoWidget = async () => {
        if (!window.Belvo) {
            alert("El widget de Belvo aún no se ha cargado. Por favor, espera un momento o recarga la página.");
            return;
        }

        setIsBelvoSyncing(true);
        try {
            const createBelvoTokenFn = httpsCallable(functions, 'createBelvoWidgetToken');
            const { data: tokenData } = await createBelvoTokenFn({ condominioId: userData.condominioId });
            const access = tokenData.access;

            const successCallback = async (link, institution) => {
                try {
                    const saveBelvoLinkFn = httpsCallable(functions, 'saveBelvoLink');
                    await saveBelvoLinkFn({
                        condominioId: userData.condominioId,
                        linkId: link,
                        institution: institution
                    });
                    alert("¡Banco vinculado con éxito! Ahora puedes sincronizar tus movimientos.");
                    setCondoData(prev => ({
                        ...prev,
                        belvoLinkId: link,
                        belvoInstitution: institution
                    }));
                } catch (err) {
                    console.error(err);
                    alert("Error al guardar la vinculación bancaria.");
                }
            };

            window.Belvo.init({
                accessToken: access,
                callback: successCallback,
                onExit: (data) => console.log("Belvo Connect cerrado:", data),
                onEvent: (data) => console.log("Belvo Evento:", data)
            });

            window.Belvo.open();

        } catch (error) {
            console.error("Error launching Belvo widget:", error);
            alert("Error al iniciar conexión bancaria: " + error.message);
        } finally {
            setIsBelvoSyncing(false);
        }
    };

    const handleSyncBelvoTransactions = async () => {
        setIsBelvoSyncing(true);
        try {
            const syncBelvoFn = httpsCallable(functions, 'syncBelvoTransactions');
            const { data } = await syncBelvoFn({ condominioId: userData.condominioId });
            alert(`Sincronización completada con éxito. Se importaron ${data.imported} nuevos movimientos.`);
        } catch (error) {
            console.error(error);
            alert("Error al sincronizar movimientos: " + error.message);
        } finally {
            setIsBelvoSyncing(false);
        }
    };

    // --- Dashboard List State ---
    const [movimientos, setMovimientos] = useState([]);
    const [filterStatus, setFilterStatus] = useState('todos'); // todos | sin_aplicar | aplicados

    // --- Reconciliation Modal State ---
    const [showModal, setShowModal] = useState(false);
    const [selectedDeposit, setSelectedDeposit] = useState(null);

    // --- Classification Modal State ---
    const [showClasificarModal, setShowClasificarModal] = useState(false);
    const [selectedExpenseMov, setSelectedExpenseMov] = useState(null);


    const [searchTerm, setSearchTerm] = useState('');
    // ... (rest of state) ...


    const [allUsers, setAllUsers] = useState([]); // Cache for search
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userCharges, setUserCharges] = useState([]);
    const [selectedChargeIds, setSelectedChargeIds] = useState([]); // Changed to array for multi-select
    const [isProcessing, setIsProcessing] = useState(false);

    const navigate = useNavigate();

    // Fetch Base Data
    useEffect(() => {
        // 1. Fetch Movements
        const qMovs = query(collection(db, "movimientos_bancarios"), orderBy("fecha", "asc"));
        const unsubMovs = onSnapshot(qMovs, (snapshot) => {
            const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setMovimientos(docs);
        });


        // 2. Fetch Users
        const fetchUsers = async () => {
            if (!userData?.condominioId) return;
            const qUsers = query(collection(db, "usuarios"), where("condominioId", "==", userData.condominioId));
            const snap = await getDocs(qUsers);
            const usersList = snap.docs.map(d => ({
                id: d.id,
                ...d.data()
            }));
            setAllUsers(usersList);
        };
        fetchUsers();

        // 3. Fetch Condo Config (Financial Connections)
        const fetchCondoConfig = async () => {
            if (!userData?.condominioId) return;
            const docRef = doc(db, 'condominios', userData.condominioId);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                setCondoData(snap.data());
            }
        };
        fetchCondoConfig();

        return () => unsubMovs();
    }, [userData]);

    // --- Search Logic ---
    useEffect(() => {
        if (!searchTerm) {
            setFilteredUsers([]);
            return;
        }
        const lower = searchTerm.toLowerCase();
        const results = allUsers.filter(u => {
            const nombreMatch = u.nombre && u.nombre.toLowerCase().includes(lower);
            const casaMatch = u.numeroCasa && u.numeroCasa.toString().includes(lower);
            const casaPrefixMatch = u.numeroCasa && `casa ${u.numeroCasa}`.includes(lower);
            const casaPrefixMatch2 = u.numeroCasa && `casa: ${u.numeroCasa}`.includes(lower); // Optional variant

            return nombreMatch || casaMatch || casaPrefixMatch || casaPrefixMatch2;
        }).slice(0, 5); // Limit suggestions
        setFilteredUsers(results);
    }, [searchTerm, allUsers]);

    // --- Reconciliation Handlers ---

    const handleOpenConciliar = (mov) => {
        setSelectedDeposit(mov);
        setShowModal(true);
        // Reset steps
        setSearchTerm('');
        setSelectedUser(null);
        setUserCharges([]);
        setSelectedChargeIds([]);
    };

    const handleSelectUser = async (user) => {
        setSelectedUser(user);
        setFilteredUsers([]);
        setLoading(true); // Reuse loading state or local
        // Fetch pending charges for this user
        try {
            const q = query(
                collection(db, "cargos"),
                where("residenteId", "==", user.id),
                where("estatus", "in", ["pendiente", "parcial"])
            );
            const snap = await getDocs(q);
            const charges = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setUserCharges(charges);
        } catch (e) {
            console.error("Error fetching charges", e);
        }
        setLoading(false);
    };

    const handleConciliar = async () => {
        if (!selectedDeposit || !selectedUser) return;
        setIsProcessing(true);

        try {
            const batch = writeBatch(db);
            let availableAmount = selectedDeposit.monto;
            const distribution = [];
            const chargesToUpdate = userCharges.filter(c => selectedChargeIds.includes(c.id));

            // 1. Pay Selected Charges
            chargesToUpdate.forEach(charge => {
                if (availableAmount <= 0) return;

                const currentPaid = charge.montoPagado || 0;
                const saldo = charge.monto - currentPaid;
                const paymentAmount = Math.min(availableAmount, saldo);

                if (paymentAmount <= 0.01) return;

                const newPaid = currentPaid + paymentAmount;
                const isPaid = newPaid >= (charge.monto - 0.01);
                const newStatus = isPaid ? 'pagado' : 'parcial';

                const chargeRef = doc(db, "cargos", charge.id);

                const newPaymentRecord = {
                    fecha: Timestamp.now(),
                    monto: paymentAmount,
                    metodo: 'Depósito Bancario',
                    referencia: selectedDeposit.referencia || 'S/R',
                    movimientoBancarioId: selectedDeposit.id
                };

                const currentHistory = charge.historialPagos || [];

                batch.update(chargeRef, {
                    estatus: newStatus,
                    fechaPago: Timestamp.now(),
                    montoPagado: newPaid,
                    historialPagos: [...currentHistory, newPaymentRecord],
                    metodoPago: "Depósito Bancario",
                    notas: (charge.notas || '') + `\n[${moment().format('DD/MM/YYYY')}] Pago de $${paymentAmount.toFixed(2)} via depósito.`
                });

                distribution.push({
                    cargoId: charge.id,
                    montoAplicado: paymentAmount,
                    residenteId: selectedUser.id,
                    tipoCargo: charge.tipo
                });

                availableAmount -= paymentAmount;
            });

            // 2. Handle Remaining Balance (Saldo a Favor)
            if (availableAmount > 0.01) {
                // Determine user current saldoFavor or init it
                const currentSaldoFavor = selectedUser.saldoFavor || 0;
                const newSaldoFavor = currentSaldoFavor + availableAmount;

                const userRef = doc(db, "usuarios", selectedUser.id);
                batch.update(userRef, {
                    saldoFavor: newSaldoFavor
                });

                distribution.push({
                    tipo: 'saldo_favor',
                    montoAplicado: availableAmount,
                    residenteId: selectedUser.id,
                    descripcion: 'Saldo a Favor por excedente en depósito'
                });
            }

            // 3. Update Resident Status (Check remaining debts)
            // We re-calc based on what was left AFTER applying payment
            const totalDebtRemaining = userCharges.reduce((sum, c) => {
                // If this charge was paid in this batch, its debt is 0 (or reduced)
                // But userCharges is stale state. Simpler: check if user has ANY other pending charges.
                // For now, let's stick to the simple logic: if all loaded charges are paid, he is clean.
                // Ideally we query again, but let's approximate.
                return sum + (c.monto - (c.montoPagado || 0));
            }, 0) - (selectedDeposit.monto - availableAmount); // Subtract what was actually PAID to charges

            // If remaining debt is effectively 0, mark as not Moroso
            // Note: If they had debt and we paid it all, debt < 1. 
            // If they had NO debt, debt is 0.
            const isClean = totalDebtRemaining < 1.0;

            const userRef = doc(db, "usuarios", selectedUser.id);
            // Only update isMoroso if we touched charges, or just safer to update always if clean
            if (isClean) {
                batch.update(userRef, { isMoroso: false });
            }

            // 4. Update Deposit
            const depositRef = doc(db, "movimientos_bancarios", selectedDeposit.id);
            batch.update(depositRef, {
                estatus: "conciliado",
                residenteId: selectedUser.id,
                residenteNombre: selectedUser.nombre,
                distribucionPago: distribution,
                fechaConciliacion: Timestamp.now(),
                saldoFavorGenerado: availableAmount > 0.01 ? availableAmount : 0
            });

            await batch.commit();

            setShowModal(false);
            alert("¡Conciliación Exitosa!");
        } catch (e) {
            console.error("Error conciliando:", e);
            alert("Error al conciliar.");
        }
        setIsProcessing(false);
    };

    // --- Undo Reconciliation Logic (Refactored for Bulk) ---
    // Helper to add revert operations to a batch
    const addRevertOperationsToBatch = async (batch, mov) => {
        // 1. Revert Charges (Ingresos / Charges)
        if (mov.distribucionPago && Array.isArray(mov.distribucionPago)) {
            const validDists = mov.distribucionPago.filter(d => d.cargoId);
            if (validDists.length > 0) {
                const chargeDocs = await Promise.all(validDists.map(d => getDocs(query(collection(db, "cargos"), where("__name__", "==", d.cargoId)))));
                chargeDocs.forEach((snap) => {
                    if (snap.empty) return;
                    const chargeDoc = snap.docs[0];
                    const chargeData = chargeDoc.data();
                    const newHistory = (chargeData.historialPagos || []).filter(h => h.movimientoBancarioId !== mov.id);
                    const newMontoPagado = newHistory.reduce((acc, curr) => acc + (curr.monto || 0), 0);
                    batch.update(chargeDoc.ref, {
                        montoPagado: newMontoPagado,
                        estatus: newMontoPagado >= (chargeData.monto - 0.01) ? 'pagado' : (newMontoPagado > 0.01 ? 'parcial' : 'pendiente'),
                        historialPagos: newHistory
                    });
                });
            }
        }

        // 2. Revert Expenses (Egresos)
        if (mov.gastoId) {
            const gastoSnap = await getDoc(doc(db, "gastos", mov.gastoId));
            if (gastoSnap.exists()) {
                const gastoData = gastoSnap.data();
                const currentPaid = gastoData.montoPagado || 0;
                const movementAmount = Math.abs(mov.monto);
                const newPaid = Math.max(0, currentPaid - movementAmount);

                const isStillPaid = newPaid >= (gastoData.monto - 0.01);

                batch.update(gastoSnap.ref, {
                    montoPagado: newPaid,
                    estatus: isStillPaid ? 'pagado' : 'pendiente',
                    fechaPago: isStillPaid ? gastoData.fechaPago : null
                });
            }
        }

        // 3. Update Resident (Saldo a Favor Reversal)
        if (mov.residenteId) {
            const userRef = doc(db, "usuarios", mov.residenteId);
            // We set isMoroso=true just in case, or we rely on the sync later? 
            // Better to force check or just set true to be safe (they lost a payment).
            // Actually, setting isMoroso=true might be wrong if they still have 0 debt. 
            // But usually reverting a payment means debt reappears. 
            // Safer to let the daily or manual sync fix it, BUT the user requested "isMoroso" logic update recently.
            // Let's keep it safe:
            const updates = { isMoroso: true };

            if (mov.saldoFavorGenerado && mov.saldoFavorGenerado > 0) {
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    const currentSaldo = userSnap.data().saldoFavor || 0;
                    updates.saldoFavor = Math.max(0, currentSaldo - mov.saldoFavorGenerado);
                }
            }
            batch.update(userRef, updates);
        }

        // 4. Update Deposit
        const depositRef = doc(db, "movimientos_bancarios", mov.id);
        batch.update(depositRef, {
            // Force reset to 'nuevo' (unchecked) to allow manual correction or re-identifying
            estatus: 'nuevo',
            residenteId: null,
            residenteNombre: null,

            fechaConciliacion: null,
            distribucionPago: null,
            cargoId: null,
            gastoId: null,
            categoria: null,
            cuentaContableId: null,
            tipoClasificacion: null,
            saldoFavorGenerado: 0
        });
    };

    // --- Helper for fuzzy reasoning (Shared) ---
    const normalize = (s) => s ? s.toString().toLowerCase().replace(/[^a-z0-9]/g, '') : '';

    const smartClean = (s) => {
        if (!s) return '';
        let str = s.toString().toLowerCase();
        // Remove prefixes at START of string (optional space)
        str = str.replace(/^(casa|depto|departamento|apto|apartamento|unidad|lote|int|interior|ext|exterior|num|numero|no|#|torre|piso|manzana|mz|ed|edificio|condominio|condo)\s*/, '');
        // Remove remaining non-alphanumeric
        return str.replace(/[^a-z0-9]/g, '');
    };

    const findResidentMatch = (searchTerm, users) => {
        if (!searchTerm || !users) return null;
        const searchClean = smartClean(searchTerm);
        const searchStrict = normalize(searchTerm);

        // Check if search term is numeric to enable "number fuzzy match" (e.g. 23 == 023)
        const searchNum = /^\d+$/.test(searchClean) ? parseInt(searchClean, 10) : null;

        return users.find(u => {
            if (!u) return false;
            const uNameRaw = normalize(u.nombre);
            const uCasa = smartClean(u.numeroCasa);
            const uUnidad = smartClean(u.unidad);
            const uDepto = smartClean(u.departamento);

            if (searchClean.length > 0) {
                // 1. Direct String Match
                if (uCasa === searchClean) return true;
                if (uUnidad === searchClean) return true;
                if (uDepto === searchClean) return true;

                // 2. Numeric Fuzzy Match (e.g. "23" === "023")
                if (searchNum !== null) {
                    if (/^\d+$/.test(uCasa) && parseInt(uCasa, 10) === searchNum) return true;
                    if (/^\d+$/.test(uUnidad) && parseInt(uUnidad, 10) === searchNum) return true;
                    if (/^\d+$/.test(uDepto) && parseInt(uDepto, 10) === searchNum) return true;
                }
            }

            if (uNameRaw && uNameRaw === searchStrict) return true;
            if (searchStrict.length >= 3 && uNameRaw.includes(searchStrict)) return true;

            return false;
        });
    };

    const handleDeshacer = async (mov) => {
        if (!window.confirm("¿Estás seguro de deshacer esta conciliación? Se revertirán los pagos aplicados.")) return;
        setIsProcessing(true);
        try {
            const batch = writeBatch(db);
            await addRevertOperationsToBatch(batch, mov);
            await batch.commit();
            alert("Acción deshecha correctamente.");
        } catch (e) {
            console.error("Error deshaciendo:", e);
            alert("Error al deshacer.");
        } finally {
            setIsProcessing(false);
        }
    };

    // --- Bulk Actions Logic ---
    const [selectedMovIds, setSelectedMovIds] = useState(new Set());

    const toggleSelect = (id) => {
        const newSet = new Set(selectedMovIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedMovIds(newSet);
    };

    const toggleSelectAll = () => {
        if (selectedMovIds.size === sortedAndFilteredMovimientos.length) {
            setSelectedMovIds(new Set());
        } else {
            setSelectedMovIds(new Set(sortedAndFilteredMovimientos.map(m => m.id)));
        }
    };

    const handleBulkUndo = async () => {
        const count = selectedMovIds.size;
        if (count === 0) return;
        if (!window.confirm(`¿Estás seguro de DESHACER masivamente ${count} movimientos?\n\nEsta acción es irreversible y restaurará las deudas asociadas.`)) return;

        setIsProcessing(true);
        try {
            const selectedMovs = movimientos.filter(m => selectedMovIds.has(m.id));

            // Chunking to avoid Firestore Batch Limit (500 ops)
            // Each revert can be up to 5 ops. Safe chunk size = 50.
            const BATCH_SIZE = 50;
            const chunks = [];

            for (let i = 0; i < selectedMovs.length; i += BATCH_SIZE) {
                chunks.push(selectedMovs.slice(i, i + BATCH_SIZE));
            }

            let processedChunks = 0;

            for (const chunk of chunks) {
                const batch = writeBatch(db);
                // Process chunk concurrently? No, adds are async.
                for (const mov of chunk) {
                    if (mov.estatus === 'conciliado' || mov.estatus === 'clasificado') {
                        await addRevertOperationsToBatch(batch, mov);
                    }
                }
                await batch.commit();
                processedChunks++;
                console.log(`Batch ${processedChunks}/${chunks.length} committed.`);
            }

            alert(`Se han deshecho ${count} movimientos de manera exitosa.`);
            setSelectedMovIds(new Set());

        } catch (e) {
            console.error("Bulk Undo Error:", e);
            alert("Error en la operación masiva: " + e.message);
        } finally {
            setIsProcessing(false);
        }
    };


    // --- Mass Reconciliation Logic ---
    const handleMassiveConciliation = async () => {
        // Fix: Include 'nuevo' items that have resident info (re-queued items)
        const identifiedMoves = movimientos.filter(m => m.estatus === 'identificado' || (m.estatus === 'nuevo' && m.residenteIdentificado));
        const count = identifiedMoves.length;

        if (count === 0) return;
        if (!window.confirm(`¿Estás seguro de conciliar AUTOMÁTICAMENTE ${count} depósitos?\n\nSe aplicarán a las deudas más antiguas de cada residente identificado.`)) return;

        setIsProcessing(true);
        let successCount = 0;
        let errorCount = 0;
        const errorDetails = [];

        try {
            // Process sequentially to be safe with rate limits and logic
            for (const mov of identifiedMoves) {
                try {
                    // 1. Get Resident
                    // We need the resident ID from the movement (assuming 'residenteIdentificado' is name or ID? )
                    // Wait, the CSV parser puts the name in 'residenteIdentificado'. The 'residenteId' field is what matters for logic.
                    // If 'identificado' status is set, usually 'residenteId' should be set?
                    // Let's check: in CSV import we set 'residenteIdentificado' string, but NOT 'residenteId'. 
                    // AH! The CSV import sets 'residenteIdentificado' (string) -> 'estatus: identificado'. 
                    // BUT we need the actual UID to pay charges.
                    // If the CSV import didn't resolve the UID, we CANNOT conciliate automatically.
                    // Let's assume 'identificado' means we HAVE a resienteId?
                    // Reviewing import logic: 
                    // `estatus: dep.residenteIdentificado ? 'identificado' : 'nuevo'`
                    // It does NOT look up the UID. It just saves the string.
                    // THIS IS A PROBLEM. We can't pay charges without a UID.
                    // FIX: We need to resolve the UID first using the string name/unit logic.
                    // OR: We only process ones that actually have 'residenteId' set (maybe manually linked but not finalized?)
                    // Assumption for USER REQUEST: "que ya esta identificada la casa/residente".
                    // Let's try to resolve the UID on the fly if missing, using valid heuristics (Name/Unit match).

                    let targetResidenteId = mov.residenteId;
                    let targetResidenteName = mov.residenteNombre || mov.residenteIdentificado;
                    let targetResidente = null;

                    // If no ID, try to find it in `allUsers`
                    if (!targetResidenteId && targetResidenteName) {
                        const match = findResidentMatch(targetResidenteName, allUsers);

                        if (match) {
                            targetResidenteId = match.id;
                            targetResidente = match;
                        }
                    } else if (targetResidenteId) {
                        targetResidente = allUsers.find(u => u.id === targetResidenteId);
                    }

                    if (!targetResidenteId || !targetResidente) {
                        if (errorCount < 5) console.warn(`Skipping mov ${mov.id}: Resident not resolved.`);
                        errorCount++;
                        errorDetails.push(targetResidenteName || "Desconocido");
                        continue;
                    }

                    // 2. Fetch Charges for this Resident
                    const qCharges = query(
                        collection(db, "cargos"),
                        where("residenteId", "==", targetResidenteId),
                        where("estatus", "in", ["pendiente", "parcial"]),
                        orderBy("fechaVencimiento", "asc") // FIFO
                    );
                    const snapCharges = await getDocs(qCharges);
                    const charges = snapCharges.docs.map(d => ({ id: d.id, ...d.data() })); // Oldest first

                    // 3. Distribute Payment
                    const batch = writeBatch(db);
                    let available = mov.monto;
                    const distribution = [];

                    for (const charge of charges) {
                        if (available <= 0.01) break;

                        const debt = charge.monto - (charge.montoPagado || 0);
                        const payAmt = Math.min(available, debt);

                        if (payAmt > 0) {
                            const newPaid = (charge.montoPagado || 0) + payAmt;
                            const isFull = newPaid >= (charge.monto - 0.01);

                            const payRef = {
                                fecha: Timestamp.now(),
                                monto: payAmt,
                                metodo: 'Depósito Automático',
                                referencia: mov.referencia || 'Mass Sync',
                                movimientoBancarioId: mov.id
                            };

                            batch.update(doc(db, "cargos", charge.id), {
                                montoPagado: newPaid,
                                estatus: isFull ? 'pagado' : 'parcial',
                                historialPagos: [...(charge.historialPagos || []), payRef],
                                fechaPago: Timestamp.now(), // update last payment date
                                metodoPago: 'Depósito Automático'
                            });

                            distribution.push({
                                cargoId: charge.id,
                                montoAplicado: payAmt,
                                residenteId: targetResidenteId,
                                tipoCargo: charge.tipo
                            });

                            available -= payAmt;
                        }
                    }

                    // 4. Handle Surplus (Saldo a Favor)
                    let surplus = 0;
                    if (available > 0.01) {
                        surplus = available;
                        const newSaldo = (targetResidente.saldoFavor || 0) + surplus;
                        batch.update(doc(db, "usuarios", targetResidenteId), {
                            saldoFavor: newSaldo
                        });
                        distribution.push({
                            tipo: 'saldo_favor',
                            montoAplicado: surplus,
                            residenteId: targetResidenteId,
                            descripcion: 'Excedente (Mass Sync)'
                        });
                    }

                    // 5. Update Resident Status (Simple check: if we paid all loaded charges AND no error, assume clean? 
                    // To be safe, re-check isMoroso only if we cleared everything locally? 
                    // Let's update isMoroso to false if we paid all charges found. 
                    // eslint-disable-next-line no-unused-vars
                    const allPaidLocally = charges.every(c => {
                        // Check if this charge was fully paid in this loop?
                        // Simplification: if available >= 0 at end, we paid everything found.
                        return true;
                    });

                    if (available >= -0.01 && charges.length > 0) {
                        // We covered all found charges (limit 50? query didn't limit, so yes all)
                        batch.update(doc(db, "usuarios", targetResidenteId), {
                            isMoroso: false
                        });
                    }


                    // 6. Update Movement
                    batch.update(doc(db, "movimientos_bancarios", mov.id), {
                        estatus: "conciliado",
                        residenteId: targetResidenteId,
                        residenteNombre: targetResidente.nombre,
                        fechaConciliacion: Timestamp.now(),
                        distribucionPago: distribution,
                        saldoFavorGenerado: surplus
                    });

                    await batch.commit();
                    successCount++;

                } catch (innerErr) {
                    console.error("Error processing single mov:", mov, innerErr);
                    errorCount++;
                }
            }

            if (errorDetails.length > 0) {
                alert(`Proceso finalizado.\n\n✅ Conciliados: ${successCount}\n⚠️ Omitidos: ${errorCount}\n\nDetalles (ejemplos):\n- No se encontró residente para: "${errorDetails.slice(0, 3).join('", "')}"...`);
            } else {
                alert(`Proceso finalizado.\n\n✅ Conciliados: ${successCount}`);
            }

        } catch (e) {
            console.error("Critical batch error:", e);
            alert("Error crítico en el proceso masivo.");
        } finally {
            setIsProcessing(false);
        }
    };

    // --- Import Logic ---
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const text = evt.target.result;
            const { headers, data } = parseCSV(text);
            setHeaders(headers);
            setCsvData(data);

            // Auto-guess mapping
            const newMapping = { ...mapping };
            headers.forEach(h => {
                const lower = h.toLowerCase();
                if (lower.includes('fecha') || lower.includes('date')) newMapping.fecha = h;
                if (lower.includes('desc') || lower.includes('concept')) newMapping.descripcion = h;
                if (lower.includes('monto') || lower.includes('amount') || lower.includes('importe')) newMapping.monto = h;
                if (lower.includes('ref')) newMapping.referencia = h;
                if (lower.includes('residente') || lower.includes('resident') || lower.includes('usuario') || lower.includes('unit')) newMapping.residente = h;
            });
            setMapping(newMapping);
            setStep(2);
        };
        reader.readAsText(file);
    };

    const handleSaveDeposits = async () => {
        if (!processData().length) return;
        setLoading(true);

        try {
            const deposits = processData();
            const batch = writeBatch(db);

            deposits.forEach(dep => {
                const docRef = doc(collection(db, "movimientos_bancarios"));

                // Smart Resolve immediately
                let finalResidenteId = null;
                let finalResidenteNombre = null;
                let finalEstatus = 'nuevo';

                if (dep.residenteIdentificado) {
                    const match = findResidentMatch(dep.residenteIdentificado, allUsers);
                    if (match) {
                        finalResidenteId = match.id;
                        finalResidenteNombre = match.nombre;
                        finalEstatus = 'identificado'; // Green status!
                    }
                }

                batch.set(docRef, {
                    ...dep,
                    fechaCreacion: Timestamp.now(),
                    estatus: finalEstatus,
                    residenteId: finalResidenteId,
                    residenteNombre: finalResidenteNombre,
                    condominioId: userData.condominioId
                });
            });

            await batch.commit();

            alert(`${deposits.length} depósitos importados correctamente.`);
            setStep(1);
            setCsvData([]);
            setHeaders([]);
            setActiveTab('listado'); // Switch back to list after import

        } catch (error) {
            console.error("Error saving deposits:", error);
            alert("Error al guardar depósitos.");
        } finally {
            setLoading(false);
        }
    };

    const processData = () => {
        return csvData.map(row => {
            let dateVal = row[mapping.fecha];
            let parsedDate = moment(dateVal, ['DD/MM/YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY']);

            return {
                fecha: parsedDate.isValid() ? Timestamp.fromDate(parsedDate.toDate()) : Timestamp.now(),
                fechaString: dateVal,
                descripcion: row[mapping.descripcion] || '',
                monto: cleanCurrency(row[mapping.monto]),
                referencia: row[mapping.referencia] || '',
                residenteIdentificado: row[mapping.residente] || ''
            };
        }).filter(d => d.monto !== 0);
    };

    const previewData = processData();

    // --- Advanced Sorting & Filtering ---
    const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    // Filter State: { fecha: { value: '', operator: 'contains' }, ... }
    const [columnFilters, setColumnFilters] = useState({
        fecha: { value: '', operator: 'contains' },
        descripcion: { value: '', operator: 'contains' },
        monto: { value: '', operator: 'contains' },
        referencia: { value: '', operator: 'contains' },
        estatus: { value: '', operator: 'contains' }
    });

    const [activeFilterColumn, setActiveFilterColumn] = useState(null); // 'fecha', 'descripcion', etc.

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handleFilterChange = (column, field, value) => {
        setColumnFilters(prev => ({
            ...prev,
            [column]: { ...prev[column], [field]: value }
        }));
    };

    const clearFilter = (column) => {
        setColumnFilters(prev => ({
            ...prev,
            [column]: { value: '', operator: 'contains' }
        }));
        setActiveFilterColumn(null);
    };

    // Helper to get unique options for a column
    const getUniqueOptions = (key) => {
        const options = new Set();
        movimientos.forEach(m => {
            if (key === 'fecha') options.add(moment(m.fecha.toDate()).format('DD/MM/YYYY'));
            else if (key === 'estatus') options.add(m.estatus);
        });
        return Array.from(options).sort();
    };

    const sortedAndFilteredMovimientos = React.useMemo(() => {
        let data = [...movimientos];

        // 1. Status Tabs Filter
        if (filterStatus === 'sin_aplicar') {
            data = data.filter(m => m.estatus === 'nuevo' || m.estatus === 'identificado');
        } else if (filterStatus === 'aplicados') {
            data = data.filter(m => m.estatus === 'conciliado' || m.estatus === 'clasificado');
        }

        // 2. Column Filters
        Object.keys(columnFilters).forEach(key => {
            const { value, operator } = columnFilters[key];
            if (!value) return;

            const lowerFilter = value.toString().toLowerCase();

            data = data.filter(item => {
                let itemVal = '';
                let rawVal = null; // For numeric/date comparison

                if (key === 'fecha') {
                    itemVal = moment(item.fecha.toDate()).format('DD/MM/YYYY');
                    rawVal = item.fecha.toDate();
                }
                else if (key === 'descripcion') itemVal = item.descripcion || '';
                else if (key === 'monto') {
                    itemVal = item.monto.toString();
                    rawVal = item.monto;
                }
                else if (key === 'referencia') {
                    itemVal = item.residenteNombre || item.residenteIdentificado || item.categoria || item.referencia || '';
                }
                else if (key === 'estatus') itemVal = item.estatus || '';

                const lowerItem = itemVal.toString().toLowerCase();

                switch (operator) {
                    case 'contains': return lowerItem.includes(lowerFilter);
                    case 'startsWith': return lowerItem.startsWith(lowerFilter);
                    case 'endsWith': return lowerItem.endsWith(lowerFilter);
                    case 'notContains': return !lowerItem.includes(lowerFilter);
                    case 'equals': return lowerItem === lowerFilter;
                    case 'greaterThan':
                        if (key === 'monto') return rawVal > parseFloat(value);
                        if (key === 'fecha') {
                            return moment(rawVal).isAfter(moment(value, 'YYYY-MM-DD'));
                        }
                        return false;
                    case 'lessThan':
                        if (key === 'monto') return rawVal < parseFloat(value);
                        if (key === 'fecha') return moment(rawVal).isBefore(moment(value, 'YYYY-MM-DD'));
                        return false;
                    default: return lowerItem.includes(lowerFilter);
                }
            });
        });

        // 3. Sorting
        if (sortConfig.key) {
            data.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (sortConfig.key === 'fecha') {
                    aVal = a.fecha.toMillis();
                    bVal = b.fecha.toMillis();
                } else if (sortConfig.key === 'referencia') {
                    const getRefText = (item) => item.residenteNombre || item.residenteIdentificado || item.categoria || item.referencia || '';
                    aVal = getRefText(a).toLowerCase();
                    bVal = getRefText(b).toLowerCase();
                } else if (typeof aVal === 'string') {
                    aVal = aVal.toLowerCase();
                    bVal = bVal.toLowerCase();
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return data;
    }, [movimientos, filterStatus, columnFilters, sortConfig]);

    const handleOpenClasificar = (mov) => {
        setSelectedExpenseMov(mov);
        setShowClasificarModal(true);
    };

    return (
        <div className="ks-page">
            <KondoSidebarMenu activeRoute="/finanzas/banco" />

            <main className="ks-main">
                <KondoBrandHeader showBack={false} />

                <div className="ks-scroll-area" style={{ paddingBottom: '120px' }}>
                    <div className="bancaria-dashboard-container">
                        {/* --- HERO BANNER STANDARD --- */}
                        <div className="muro-header-stitch">
                            <div className="muro-hero-card">
                                <div className="muro-hero-content">
                                    <h1>Gestión Bancaria</h1>
                                    <p>Concilia movimientos, gestiona ingresos y clasifica gastos del condominio.</p>
                                </div>
                                <IoWalletOutline className="muro-hero-icon-decor" />
                            </div>
                        </div>

            {/* MESSAGE: Summary of Identified for Mass Action */}
            {sortedAndFilteredMovimientos.some(m => m.estatus === 'identificado' || (m.estatus === 'nuevo' && m.residenteIdentificado)) && (
                <div style={{ margin: '0 20px 20px 20px', padding: '15px', background: '#e0f2fe', borderRadius: '8px', border: '1px solid #bae6fd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <IoCheckmarkDoneOutline size={24} color="#0284c7" />
                        <div>
                            <strong style={{ color: '#0369a1' }}>Conciliación Masiva Disponible</strong>
                            <div style={{ fontSize: '0.9em', color: '#075985' }}>
                                Hay {sortedAndFilteredMovimientos.filter(m => m.estatus === 'identificado' || (m.estatus === 'nuevo' && m.residenteIdentificado)).length} depósitos identificados listos para procesar.
                            </div>
                        </div>
                    </div>
                    <button
                        className="primary-btn"
                        style={{ background: '#0284c7', border: 'none' }}
                        onClick={() => handleMassiveConciliation()}
                        disabled={isProcessing}
                    >
                        {isProcessing ? 'Procesando...' : `Conciliar ${sortedAndFilteredMovimientos.filter(m => m.estatus === 'identificado' || (m.estatus === 'nuevo' && m.residenteIdentificado)).length} Identificados`}
                    </button>
                </div>
            )}


            {/* --- TABS --- */}
            <div className="bancaria-tabs">
                <button
                    className={`bancaria-tab-btn ${activeTab === 'listado' ? 'active' : ''}`}
                    onClick={() => setActiveTab('listado')}
                >
                    <IoReceipt style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
                    Movimientos
                </button>
                <button
                    className={`bancaria-tab-btn ${activeTab === 'importar' ? 'active' : ''}`}
                    onClick={() => setActiveTab('importar')}
                >
                    <IoCloudUploadOutline style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
                    Importar Estado de Cuenta (CSV)
                </button>
            </div>

            {activeTab === 'listado' && (
                <div className="bancaria-card-list">
                    {/* BULK ACTION TOOLBAR */}
                    {selectedMovIds.size > 0 && (
                        <div className="bulk-actions-bar" style={{
                            marginBottom: '15px', padding: '10px 15px', background: '#e0e7ff',
                            borderRadius: '8px', border: '1px solid #c7d2fe', display: 'flex',
                            justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <span style={{ fontWeight: 'bold', color: '#4338ca' }}>
                                {selectedMovIds.size} movimientos seleccionados
                            </span>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {filterStatus !== 'sin_aplicar' && (
                                    <button
                                        className="small-action-btn danger"
                                        style={{ backgroundColor: '#dc3545', border: 'none', padding: '8px 12px', fontSize: '0.9em' }}
                                        onClick={handleBulkUndo}
                                    >
                                        <IoAlertCircleOutline style={{ marginRight: '5px' }} />
                                        Deshacer Seleccionados
                                    </button>
                                )}
                                <button
                                    style={{ background: 'transparent', border: 'none', color: '#6366f1', cursor: 'pointer', fontWeight: '500' }}
                                    onClick={() => setSelectedMovIds(new Set())}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="filters-bar" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                className={`filter-pill ${filterStatus === 'todos' ? 'active' : ''}`}
                                onClick={() => setFilterStatus('todos')}
                            >Todos</button>
                            <button
                                className={`filter-pill ${filterStatus === 'sin_aplicar' ? 'active' : ''}`}
                                onClick={() => setFilterStatus('sin_aplicar')}
                            >Sin Aplicar</button>
                            <button
                                className={`filter-pill ${filterStatus === 'aplicados' ? 'active' : ''}`}
                                onClick={() => setFilterStatus('aplicados')}
                            >Aplicados / Clasificados</button>
                        </div>

                        {/* Rows per page selector */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '0.85em', color: '#666' }}>Mostrar:</span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                style={{
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    fontSize: '0.9em',
                                    background: '#f9fafb',
                                    color: '#374151',
                                    cursor: 'pointer',
                                    outline: 'none'
                                }}
                            >
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={999999}>Todos</option>
                            </select>
                        </div>
                    </div>

                    {/* TABLE VIEW FOR DESKTOP */}
                    <div className="table-responsive hide-on-mobile">
                        <table className="depositos-table">
                            <thead>
                                <tr className="sortable-headers">
                                    <th style={{ width: '40px', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedMovIds.size > 0 && selectedMovIds.size === sortedAndFilteredMovimientos.length}
                                            ref={input => {
                                                if (input) {
                                                    input.indeterminate = selectedMovIds.size > 0 && selectedMovIds.size < sortedAndFilteredMovimientos.length;
                                                }
                                            }}
                                            onChange={toggleSelectAll}
                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                        />
                                    </th>
                                    {[
                                        { key: 'fecha', label: 'Fecha', type: 'date' },
                                        { key: 'descripcion', label: 'Descripción', type: 'text' },
                                        { key: 'monto', label: 'Monto', type: 'number' },
                                        { key: 'referencia', label: 'Ref / Residente', type: 'text' },
                                        { key: 'estatus', label: 'Estatus', type: 'select' }
                                    ].map(col => (
                                        <th key={col.key} style={{ position: 'relative' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span onClick={() => handleSort(col.key)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    {col.label}
                                                    {sortConfig.key === col.key && (sortConfig.direction === 'asc' ? <IoCaretUpOutline size={12} /> : <IoCaretDownOutline size={12} />)}
                                                </span>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setActiveFilterColumn(activeFilterColumn === col.key ? null : col.key); }}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: columnFilters[col.key].value ? '#0284c7' : '#9ca3af' }}
                                                >
                                                    <IoFunnelOutline size={16} />
                                                </button>
                                            </div>

                                            {/* FILTER POPOVER */}
                                            {activeFilterColumn === col.key && (
                                                <div className="filter-popover" style={{
                                                    position: 'absolute', top: '100%', left: 0, zIndex: 1000,
                                                    background: 'white', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                    borderRadius: '8px', padding: '12px', width: '220px', marginTop: '5px'
                                                }}>
                                                    <div style={{ fontSize: '0.85em', fontWeight: 'bold', marginBottom: '8px', color: '#374151' }}>Filtrar {col.label}</div>

                                                    {/* Operator Selector */}
                                                    <select
                                                        value={columnFilters[col.key].operator}
                                                        onChange={(e) => handleFilterChange(col.key, 'operator', e.target.value)}
                                                        style={{ width: '100%', marginBottom: '8px', padding: '4px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.9em' }}
                                                    >
                                                        <option value="contains">Contiene</option>
                                                        <option value="equals">Es igual a</option>
                                                        <option value="notContains">No contiene</option>
                                                        {(col.type === 'number' || col.type === 'date') && (
                                                            <>
                                                                <option value="greaterThan">Mayor A ({col.type === 'date' ? 'Posterior' : '>'})</option>
                                                                <option value="lessThan">Menor A ({col.type === 'date' ? 'Anterior' : '<'})</option>
                                                            </>
                                                        )}
                                                        <option value="startsWith">Empieza con</option>
                                                        <option value="endsWith">Termina con</option>
                                                    </select>

                                                    {/* Value Input OR Select */}
                                                    {(col.key === 'estatus' || (col.key === 'fecha' && columnFilters[col.key].operator === 'equals')) ? (
                                                        <select
                                                            value={columnFilters[col.key].value}
                                                            onChange={(e) => handleFilterChange(col.key, 'value', e.target.value)}
                                                            style={{ width: '100%', marginBottom: '12px', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.9em' }}
                                                        >
                                                            <option value="">Seleccionar...</option>
                                                            {getUniqueOptions(col.key).map(opt => (
                                                                <option key={opt} value={opt}>{opt}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <input
                                                            type={col.type === 'date' && (columnFilters[col.key].operator === 'greaterThan' || columnFilters[col.key].operator === 'lessThan') ? 'date' : 'text'}
                                                            placeholder="Valor..."
                                                            value={columnFilters[col.key].value}
                                                            onChange={(e) => handleFilterChange(col.key, 'value', e.target.value)}
                                                            style={{ width: '100%', marginBottom: '12px', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.9em' }}
                                                            autoFocus
                                                        />
                                                    )}

                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); clearFilter(col.key); }}
                                                            style={{ padding: '4px 8px', fontSize: '0.8em', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
                                                        >
                                                            Limpiar
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setActiveFilterColumn(null); }}
                                                            className="primary-btn"
                                                            style={{ padding: '4px 8px', fontSize: '0.8em', minWidth: 'auto' }}
                                                        >
                                                            Aplicar
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                    <th>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedAndFilteredMovimientos.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(mov => (
                                    <tr key={mov.id} className={selectedMovIds.has(mov.id) ? 'selected-row' : ''} style={selectedMovIds.has(mov.id) ? { backgroundColor: '#f0fdf4' } : {}}>
                                        <td style={{ textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedMovIds.has(mov.id)}
                                                onChange={() => toggleSelect(mov.id)}
                                                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                            />
                                        </td>
                                        <td>{moment(mov.fecha.toDate()).format('DD/MM/YYYY')}</td>
                                        <td>{mov.descripcion}</td>
                                        <td style={{ fontWeight: 'bold' }} className={mov.monto >= 0 ? "text-success" : "text-danger"}>
                                            ${new Intl.NumberFormat('es-MX').format(mov.monto)}
                                        </td>
                                        <td>
                                            {mov.residenteIdentificado || mov.residenteNombre ? (
                                                <span className="badge-identificado">
                                                    <IoCheckmarkDoneOutline /> {mov.residenteNombre || mov.residenteIdentificado}
                                                </span>
                                            ) : (mov.categoria ? (
                                                <span className="badge-gasto" style={{ background: '#fee2e2', color: '#b91c1c', padding: '2px 8px', borderRadius: '12px', fontSize: '0.85em' }}>
                                                    <IoReceipt /> {mov.categoria}
                                                </span>
                                            ) : mov.referencia)}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${mov.estatus === 'conciliado' || mov.estatus === 'clasificado' ? 'pagado' : 'pendiente'}`}>
                                                {mov.estatus.toUpperCase()}
                                            </span>
                                        </td>
                                        <td>
                                            {mov.estatus !== 'conciliado' && mov.estatus !== 'clasificado' && (
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <button
                                                        className={`small-action-btn ${!mov.residenteIdentificado && !mov.residenteNombre ? 'ghost-blue' : ''}`}
                                                        style={(!mov.residenteIdentificado && !mov.residenteNombre) ? { background: 'transparent', border: '1px solid #3b82f6', color: '#3b82f6' } : {}}
                                                        onClick={(e) => { e.stopPropagation(); handleOpenConciliar(mov); }}
                                                    >
                                                        Conciliar
                                                    </button>

                                                    <button
                                                        className={`small-action-btn ${(mov.residenteIdentificado || mov.residenteNombre) ? 'ghost-purple' : 'btn-purple'}`}
                                                        style={
                                                            (mov.residenteIdentificado || mov.residenteNombre)
                                                                ? { background: 'transparent', border: '1px solid #8b5cf6', color: '#8b5cf6' }
                                                                : { background: '#8b5cf6', color: 'white' }
                                                        }
                                                        onClick={(e) => { e.stopPropagation(); handleOpenClasificar(mov); }}
                                                    >
                                                        Clasificar
                                                    </button>
                                                </div>
                                            )}
                                            {(mov.estatus === 'conciliado' || mov.estatus === 'clasificado') && (
                                                <button
                                                    className="small-action-btn danger"
                                                    style={{ backgroundColor: '#dc3545', marginLeft: '5px' }}
                                                    onClick={(e) => { e.stopPropagation(); handleDeshacer(mov); }}
                                                >
                                                    Deshacer
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {sortedAndFilteredMovimientos.length === 0 && (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                                            No hay movimientos en esta vista.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* CARD VIEW FOR MOBILE */}
                    <div className="bancaria-mobile-list show-on-mobile-flex">
                        {sortedAndFilteredMovimientos.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(mov => (
                            <div
                                key={mov.id}
                                className={`bancaria-card-premium ${selectedMovIds.has(mov.id) ? 'selected' : ''}`}
                            >
                                <div className="bancaria-card-header">
                                    <div className="bancaria-card-header-left">
                                        <input
                                            type="checkbox"
                                            checked={selectedMovIds.has(mov.id)}
                                            onChange={() => toggleSelect(mov.id)}
                                            className="bancaria-card-checkbox"
                                        />
                                        <span className="bancaria-card-date">
                                            {moment(mov.fecha.toDate()).format('DD/MM/YYYY')}
                                        </span>
                                    </div>
                                    <span className={`status-badge ${mov.estatus === 'conciliado' || mov.estatus === 'clasificado' ? 'pagado' : 'pendiente'}`}>
                                        {mov.estatus.toUpperCase()}
                                    </span>
                                </div>

                                <div className="bancaria-card-body">
                                    <div className="bancaria-card-icon-wrapper" style={{
                                        background: mov.monto >= 0 ? '#e0f2fe' : '#fee2e2',
                                        color: mov.monto >= 0 ? '#0284c7' : '#ef4444'
                                    }}>
                                        {mov.monto >= 0 ? <IoWalletOutline size={20} /> : <IoReceipt size={20} />}
                                    </div>
                                    <div className="bancaria-card-info">
                                        <div className="bancaria-card-desc">{mov.descripcion}</div>
                                        <div className="bancaria-card-ref">
                                            {mov.residenteIdentificado || mov.residenteNombre ? (
                                                <span className="badge-identificado">
                                                    <IoCheckmarkDoneOutline style={{ marginRight: '4px', verticalAlign: 'middle' }} /> 
                                                    {mov.residenteNombre || mov.residenteIdentificado}
                                                </span>
                                            ) : (mov.categoria ? (
                                                <span className="badge-gasto" style={{ background: '#fee2e2', color: '#b91c1c', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8em', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <IoReceipt size={12} /> {mov.categoria}
                                                </span>
                                            ) : (
                                                <span className="bancaria-card-ref-text">Ref: {mov.referencia || 'Sin Referencia'}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={`bancaria-card-monto ${mov.monto >= 0 ? 'text-success' : 'text-danger'}`} style={{
                                        fontWeight: '800',
                                        fontSize: '1.1rem',
                                        alignSelf: 'center',
                                        marginLeft: 'auto'
                                    }}>
                                        ${new Intl.NumberFormat('es-MX').format(mov.monto)}
                                    </div>
                                </div>

                                <div className="bancaria-card-actions">
                                    {mov.estatus !== 'conciliado' && mov.estatus !== 'clasificado' && (
                                        <div className="actions-buttons" style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                            <button
                                                className={`premium-action-btn ${!mov.residenteIdentificado && !mov.residenteNombre ? 'ghost-blue' : ''}`}
                                                style={{ flex: 1, justifyContent: 'center' }}
                                                onClick={(e) => { e.stopPropagation(); handleOpenConciliar(mov); }}
                                            >
                                                Conciliar
                                            </button>
                                            <button
                                                className={`premium-action-btn ${(mov.residenteIdentificado || mov.residenteNombre) ? 'ghost-purple' : 'btn-purple'}`}
                                                style={{ flex: 1, justifyContent: 'center' }}
                                                onClick={(e) => { e.stopPropagation(); handleOpenClasificar(mov); }}
                                            >
                                                Clasificar
                                            </button>
                                        </div>
                                    )}
                                    {(mov.estatus === 'conciliado' || mov.estatus === 'clasificado') && (
                                        <button
                                            className="premium-action-btn delete"
                                            style={{ width: '100%', justifyContent: 'center' }}
                                            onClick={(e) => { e.stopPropagation(); handleDeshacer(mov); }}
                                        >
                                            Deshacer Conciliación
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}

                        {sortedAndFilteredMovimientos.length === 0 && (
                            <div className="empty-state" style={{ textAlign: 'center', padding: '30px', color: '#888', width: '100%' }}>
                                No hay movimientos en esta vista.
                            </div>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px', padding: '0 10px' }}>
                        <div style={{ fontSize: '0.9em', color: '#64748b' }}>
                            Mostrando {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, sortedAndFilteredMovimientos.length)} de {sortedAndFilteredMovimientos.length} resultados
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    style={{ padding: '5px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', background: currentPage === 1 ? '#f1f5f9' : 'white', cursor: currentPage === 1 ? 'default' : 'pointer' }}
                                >
                                    Anterior
                                </button>
                                <span style={{ display: 'flex', alignItems: 'center', padding: '0 5px', fontSize: '0.9em', color: '#64748b' }}>
                                    Página {currentPage}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => (p * itemsPerPage < sortedAndFilteredMovimientos.length ? p + 1 : p))}
                                    disabled={currentPage * itemsPerPage >= sortedAndFilteredMovimientos.length}
                                    style={{ padding: '5px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', background: currentPage * itemsPerPage >= sortedAndFilteredMovimientos.length ? '#f1f5f9' : 'white', cursor: currentPage * itemsPerPage >= sortedAndFilteredMovimientos.length ? 'default' : 'pointer' }}
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- TAB: IMPORTAR (Wizard) --- */}
            {activeTab === 'importar' && (
                <>
                    {step === 1 && (
                        <div className="upload-box">
                            {/* PREMIUM FEATURE CHECK */}
                            {!(condoData?.saas_addons || []).includes('saas_bank_recon') ? (
                                <div className="premium-upsell-banner">
                                    <IoWalletOutline size={40} className="icon-pulse" />
                                    <div>
                                        <h4>Automatiza tu conciliación</h4>
                                        <p>Agrega la sincronización bancaria para importar movimientos automáticamente sin archivos CSV.</p>
                                        <button className="upsell-btn" onClick={() => window.open(`https://wa.me/5211234567890?text=Hola!%20Me%20interesa%20contratar%20el%20módulo%20de%20Conciliación%20Bancaria%20Automática%20para%20el%20condominio%20${encodeURIComponent(condoData?.nombre || '')}`, '_blank')}>
                                            Contactar a Ventas
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ marginBottom: '20px', padding: '10px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', justifyContent: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={condoData.useFinancialConnections || false}
                                            onChange={async (e) => {
                                                const newVal = e.target.checked;
                                                const docRef = doc(db, 'condominios', userData.condominioId);
                                                await import('firebase/firestore').then(mod => mod.updateDoc(docRef, { useFinancialConnections: newVal }));
                                                setCondoData({ ...condoData, useFinancialConnections: newVal });
                                            }}
                                        />
                                        <strong>Activar Sincronización Bancaria (Automatizada)</strong>
                                    </label>
                                </div>
                            )}

                            {/* SHOW UI ONLY IF ENABLED */}
                            {condoData?.useFinancialConnections && (condoData?.saas_addons || []).includes('saas_bank_recon') ? (
                                <div className="bank-connect-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px', padding: '20px', border: '1px solid #e2e8f0', borderRadius: '12px', background: 'white', marginTop: '10px' }}>
                                    <IoWalletOutline size={60} color="#4f46e5" />
                                    <h3 style={{ margin: 0 }}>Conexión Bancaria Automática</h3>

                                    {condoData.belvoLinkId ? (
                                        <div className="connected-state" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
                                            <p className="text-success" style={{ fontWeight: 'bold', color: '#166534', margin: 0, display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                <IoCheckmarkDoneOutline size={20} /> Banco Vinculado exitosamente
                                            </p>
                                            <p style={{ fontSize: '0.9em', color: '#475569', margin: 0 }}>
                                                <strong>Portal:</strong> {condoData.belvoInstitution || 'Banco en línea'}
                                            </p>
                                            {condoData.lastTransactionSync && (
                                                <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0 }}>
                                                    Última sincronización: {new Date(condoData.lastTransactionSync?.seconds * 1000 || condoData.lastTransactionSync).toLocaleString()}
                                                </p>
                                            )}

                                            <button
                                                className="primary-btn"
                                                disabled={isBelvoSyncing}
                                                onClick={handleSyncBelvoTransactions}
                                                style={{ marginTop: '5px', padding: '10px 20px', background: '#4f46e5', border: 'none', color: 'white', borderRadius: '8px', fontWeight: 'bold', cursor: isBelvoSyncing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                            >
                                                {isBelvoSyncing ? 'Sincronizando...' : 'Sincronizar Movimientos'}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="connect-state" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
                                            <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem' }}>Vincula tu cuenta bancaria de forma segura para importar transacciones de forma automática.</p>
                                            <button
                                                className="primary-btn"
                                                disabled={isBelvoSyncing || !belvoScriptLoaded}
                                                onClick={handleOpenBelvoWidget}
                                                style={{ padding: '10px 20px', background: '#0ea5e9', border: 'none', color: 'white', borderRadius: '8px', fontWeight: 'bold', cursor: (isBelvoSyncing || !belvoScriptLoaded) ? 'not-allowed' : 'pointer' }}
                                            >
                                                {isBelvoSyncing ? 'Iniciando conexión...' : 'Vincular Cuenta Bancaria'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="csv-upload-container">
                                    <div className="icon-wrapper">
                                        <IoCloudUploadOutline size={64} color="#6366f1" />
                                    </div>
                                    <h3>Sube tu archivo CSV</h3>
                                    <p>Funciona con cualquier banco (BBVA, Santander, etc.)</p>

                                    <label className="custom-file-upload">
                                        <input type="file" accept=".csv" onChange={handleFileUpload} />
                                        <span className="upload-btn-text">Seleccionar Archivo</span>
                                    </label>
                                </div>

                            )}
                        </div>
                    )}

                    {step === 2 && (
                        <div className="mapping-box">
                            <h3>Confirma las columnas</h3>
                            <div className="mapping-grid">
                                <div className="map-item">
                                    <label>Fecha:</label>
                                    <select value={mapping.fecha} onChange={e => setMapping({ ...mapping, fecha: e.target.value })}>
                                        <option value="">Selecciona...</option>
                                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                </div>
                                <div className="map-item">
                                    <label>Descripción / Concepto:</label>
                                    <select value={mapping.descripcion} onChange={e => setMapping({ ...mapping, descripcion: e.target.value })}>
                                        <option value="">Selecciona...</option>
                                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                </div>
                                <div className="map-item">
                                    <label>Monto / Importe:</label>
                                    <select value={mapping.monto} onChange={e => setMapping({ ...mapping, monto: e.target.value })}>
                                        <option value="">Selecciona...</option>
                                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                </div>
                                <div className="map-item">
                                    <label>Referencia (Opcional):</label>
                                    <select value={mapping.referencia} onChange={e => setMapping({ ...mapping, referencia: e.target.value })}>
                                        <option value="">Selecciona...</option>
                                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                </div>
                                <div className="map-item">
                                    <label>Residente / Unidad (Opcional):</label>
                                    <p style={{ fontSize: '0.8em', color: '#666' }}>Si ya identificaste algunos en el CSV</p>
                                    <select value={mapping.residente} onChange={e => setMapping({ ...mapping, residente: e.target.value })}>
                                        <option value="">Selecciona...</option>
                                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="wizard-actions">
                                <button className="primary-btn" onClick={() => setStep(3)}>Continuar</button>
                                <button className="secondary-btn" onClick={() => setStep(1)}>Atrás</button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="review-box">
                            <h3>Revisión de Datos ({previewData.length} registros)</h3>
                            <div className="table-responsive">
                                <table className="depositos-table">
                                    <thead>
                                        <tr>
                                            <th>Fecha</th>
                                            <th>Descripción</th>
                                            <th>Monto</th>
                                            <th>Ref / Residente</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewData.map((row, idx) => (
                                            <tr key={idx}>
                                                <td>{moment(row.fecha.toDate()).format('DD/MM/YYYY')}</td>
                                                <td>{row.descripcion}</td>
                                                <td className={row.monto > 0 ? 'text-success' : 'text-danger'}>
                                                    ${new Intl.NumberFormat('es-MX').format(row.monto)}
                                                </td>
                                                <td>
                                                    {row.residenteIdentificado ? (
                                                        <span className="badge-identificado"><IoCheckmarkDoneOutline /> {row.residenteIdentificado}</span>
                                                    ) : row.referencia}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="actions-row">
                                <button className="primary-btn" onClick={handleSaveDeposits} disabled={loading}>
                                    {loading ? 'Guardando...' : <><IoSaveOutline /> Confirmar e Importar</>}
                                </button>
                                <button className="secondary-btn" onClick={() => setStep(2)}>Atrás</button>
                            </div>
                        </div>
                    )}
                </>
            )
            }

            {/* --- RECONCILIATION MODAL --- */}
            {
                showModal && selectedDeposit && (
                    <div className="modal-overlay">
                        <div className="modal-content" style={{ maxWidth: '600px' }}>
                            <div className="modal-header">
                                <h3>Conciliar Depósito</h3>
                                <button onClick={() => setShowModal(false)} className="close-btn"><IoClose /></button>
                            </div>

                            {/* Deposit Summary */}
                            <div className="deposit-summary-card">
                                <div className="row">
                                    <span className="label">Monto:</span>
                                    <span className="val-money">${new Intl.NumberFormat('es-MX').format(selectedDeposit.monto)}</span>
                                </div>
                                <div className="row">
                                    <span className="label">Referencia:</span>
                                    <span className="val">{selectedDeposit.referencia || 'N/A'}</span>
                                </div>
                                <div className="row">
                                    <span className="label">Descripción:</span>
                                    <span className="val-desc">{selectedDeposit.descripcion}</span>
                                </div>
                            </div>

                            {/* Step 1: Find User */}
                            <div className="reconcile-step">
                                <h4>1. Identificar Residente</h4>
                                {selectedUser ? (
                                    <div className="selected-user-pill">
                                        <IoPerson />
                                        <span>{selectedUser.nombre} ({selectedUser.numeroCasa})</span>
                                        <button onClick={() => { setSelectedUser(null); setUserCharges([]); setSelectedChargeIds([]); }} className="change-user-btn">Cambiar</button>
                                    </div>
                                ) : (
                                    <div className="search-box">
                                        <IoSearchOutline className="search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Buscar por nombre o # casa..."
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                            className="search-input"
                                        />
                                        {filteredUsers.length > 0 && (
                                            <div className="autocomplete-results">
                                                {filteredUsers.map(u => (
                                                    <div key={u.id} className="result-item" onClick={() => handleSelectUser(u)}>
                                                        {u.nombre} - Casa {u.numeroCasa}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Step 2: Select Charge */}
                            {selectedUser && (
                                <div className="reconcile-step">
                                    <h4>2. Seleccionar Cuota a Pagar</h4>
                                    {loading ? (
                                        <p>Cargando adeudos...</p>
                                    ) : userCharges.length === 0 ? (
                                        <div style={{ padding: '15px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', color: '#92400e' }}>
                                            <IoAlertCircleOutline style={{ verticalAlign: 'middle', marginRight: '5px' }} size={20} />
                                            Este residente no tiene deudas pendientes. El monto total se registrará como <strong>Saldo a Favor</strong>.
                                        </div>
                                    ) : (
                                        <div className="charges-list">
                                            {userCharges.map(charge => {
                                                const saldo = getSaldo(charge);
                                                const isSelected = selectedChargeIds.includes(charge.id);
                                                return (
                                                    <div
                                                        key={charge.id}
                                                        className={`charge-card ${isSelected ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setSelectedChargeIds(prev =>
                                                                prev.includes(charge.id)
                                                                    ? prev.filter(id => id !== charge.id)
                                                                    : [...prev, charge.id]
                                                            );
                                                        }}
                                                    >
                                                        <div className="charge-info">
                                                            <span className="charge-title">{charge.tipo}</span>
                                                            <span className="charge-desc">{charge.descripcion}</span>
                                                            {charge.montoPagado > 0 && <span className="partial-badge">Parcial: ${charge.montoPagado} pagados</span>}
                                                        </div>
                                                        <div className="charge-amount">
                                                            <div className="total-amt">${new Intl.NumberFormat('es-MX').format(charge.monto)}</div>
                                                            <div className="saldo-amt">Saldo: ${new Intl.NumberFormat('es-MX').format(saldo)}</div>
                                                        </div>
                                                        {isSelected && <IoCheckmarkDoneOutline className="check-icon" />}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Actions */}
                            <div className="modal-actions-footer">
                                <div className="selection-summary" style={{ marginBottom: '10px', fontSize: '0.9em', color: '#666' }}>
                                    {selectedChargeIds.length} cargos seleccionados
                                </div>
                                <button
                                    className="confirm-btn"
                                    disabled={isProcessing || !selectedUser}
                                    onClick={handleConciliar}
                                >
                                    {isProcessing ? 'Procesando...' : <><IoSaveOutline /> Confirmar Conciliación</>}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }



            {/* --- CLASIFICAR MODAL --- */}
            {
                showClasificarModal && selectedExpenseMov && (
                    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9999 }}>
                        <ClasificarGastoModal
                            mov={selectedExpenseMov}
                            onClose={() => setShowClasificarModal(false)}
                            onSuccess={() => {
                                // Refresh logic if needed, but snapshot handles it
                            }}
                        />
                    </div>
                )
            }
                    </div>
                </div>
            </main>

            <KondoBottomNav activeRoute="/finanzas/banco" />
        </div >
    );
}

export default GestionBancariaScreen;
